import React, { useCallback, useEffect, useState } from "react";
import styles from "./PlatformPage.module.scss";
import { client } from "api/client";
import { StreamingProvider } from "@switcherstudio/switcher-api-client";
import { useIsMountedRef } from "hooks/useIsMountedRef";
import { usePlatformActions } from "hooks/usePlatformActions";
import { PlatformId } from "../types";
import { useNavigate } from "react-router-dom";
import { FacebookContainer } from "./facebook/FacebookContainer";
import { YouTubeContainer } from "./youtube/YoutubeContainer";
import { TwitchContainer } from "./twitch/TwitchContainer";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ConfigureExternalChannelSettingsContainer } from "./external-channel-settings/ConfigureExternalChannelSettingsContainer";
import { Switchboard } from "./switchboard/Switchboard";
import { AppDispatch } from "store/store";
import { useParams } from "hooks/useParams";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { AttentionModal } from "components/modal/AttentionModal";

export const PlatformPage: React.FC = () => {
    const { platformId, destinationType } = useParams();
    const { t } = useTranslation();
    const isMountedRef = useIsMountedRef();
    const navigate = useNavigate();
    const [platform, setPlatform] = useState<StreamingProvider>();
    const { unlink } = usePlatformActions(platformId as PlatformId);
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        async function run() {
            try {
                const response =
                    await client.streamingProviders_GetStreamingProvider(
                        platformId
                    );
                if (isMountedRef.current) {
                    setPlatform(response);
                }
            } catch (e) {
                navigate(`/platforms`, {
                    replace: true
                });
            }
        }

        if (isMountedRef.current) {
            if (!platform) {
                run();
            } else if (!platform.IsLinked) {
                navigate(`/platforms/connect/${platformId}`, {
                    replace: true
                });
                return;
            }
        }
    }, [isMountedRef, navigate, platform, platformId]);

    const disconnect = useCallback(async () => {
        await unlink();
        setPlatform((p) => {
            return { ...p, IsLinked: false };
        });
    }, [unlink, setPlatform]);

    const closeAttentionModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const openAttentionModal = useCallback(
        () =>
            dispatch(
                setActiveModal({
                    id: Modals.AttentionModal,
                    type: Modals.AttentionModal,
                    component: (
                        <AttentionModal
                            isOpen={true}
                            setIsOpen={closeAttentionModal}
                            handleCancel={closeAttentionModal}
                            hasContinueButton={true}
                            handleContinue={disconnect}
                            continueText={t("buttons:confirm")}
                        >
                            <div>
                                {t("platforms:confirm-account-disconnect")}
                            </div>
                        </AttentionModal>
                    )
                })
            ),
        [closeAttentionModal, dispatch, t, disconnect]
    );

    return (
        <>
            {platform !== undefined && (
                <div className="row">
                    <div className="col-lg-12">
                        <div className={styles["header"]}>
                            <h4>{platform?.Name}</h4>
                            <div className={styles["buttons"]}>
                                <button
                                    className="btn btn-outline-danger"
                                    onClick={openAttentionModal}
                                >
                                    {t("platforms:disconnect-account-button")}
                                </button>
                            </div>
                        </div>
                        {(function () {
                            if (platform.IsLinked) {
                                switch (platformId) {
                                    case PlatformId.Facebook:
                                        return (
                                            <FacebookContainer
                                                platform={platform}
                                                destinationType={
                                                    destinationType
                                                }
                                            />
                                        );
                                    case PlatformId.Youtube:
                                        return (
                                            <YouTubeContainer
                                                platform={platform}
                                            />
                                        );
                                    case PlatformId.Twitch:
                                        return (
                                            <TwitchContainer
                                                platform={platform}
                                            />
                                        );
                                    case PlatformId.BoxCast:
                                        return;
                                    case PlatformId.SwitchboardLive:
                                        return <Switchboard />;
                                    // url-based RTMP integrations
                                    case PlatformId.MsStream:
                                    case PlatformId.AmazonLive:
                                    case PlatformId.AzureMediaServices:
                                    case PlatformId.Caast:
                                    case PlatformId.Instafeed:
                                        return (
                                            <ConfigureExternalChannelSettingsContainer
                                                platform={platform}
                                            />
                                        );
                                    default:
                                        return (
                                            <div
                                                className="alert alert-info"
                                                role="alert"
                                            >
                                                {t(
                                                    "platforms:unsupported-platform"
                                                )}
                                            </div>
                                        );
                                }
                            }
                        })()}
                    </div>
                </div>
            )}
        </>
    );
};
